<template>
  <teleport to="body">
    <ag-loading v-if="loading" class="global-loading" />
  </teleport>

  <EditPanel
    v-model="visible"
    title="Import App Group"
    :form-component="formComponent"
    :form="formData"
    form-type="appGroupImport"
    :show-enabled="false"
    @save="onSave"
    @cancel="onCancel"
  />

  <input
    type="file"
    ref="fileInput"
    accept=".json"
    style="display: none"
    @change="handleFileUpload"
  />
</template>

<script setup>
import { defineExpose, ref, reactive } from "vue";
import Swal from "sweetalert2";
import { useStore } from "@/store";
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import EditPanel from "@/components/ag-edit-panel/index.vue";
import ImportForm from "@/components/ag-edit-panel/forms/app-group-import-form.vue";
import AgLoading from "@/components/ag-loading/index.vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const store = useStore();
const visible = ref(false);
const fileInput = ref(null);
const loading = ref(false);
const router = useRouter();

const formData = reactive({
  name: "",
  fileName: "",
  importedData: null,
  importVersion: null,
  importOptions: {
    createNew: null,
    existingAppGroupId: null,
    includeProcess: false,
    includeFileAccess: false,
    includeRegistryAccess: false,
    includeCodeLoad: false,
    includeChildLaunch: false,
  },
});

const formComponent = ImportForm;

const open = () => {
  if (fileInput.value) fileInput.value.value = "";
  fileInput.value?.click();
};

const handleFileUpload = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  formData.fileName = file.name;

  const reader = new FileReader();
 reader.onload = (e) => {
  try {
    const raw = JSON.parse(e.target.result);

    if (!raw.Version || !raw.AppGroup) {
      return Swal.fire("Error", "Invalid file format. Please use a file exported from this application.", "error");
    }

    if (raw.Version !== "1.0") {
      return Swal.fire("Error", `Unsupported file version: ${raw.Version}.`, "error");
    }

    formData.importVersion = raw.Version;
    const parsed = raw.AppGroup;

    formData.importedData = parsed;
    formData.name = parsed?.Name || parsed?.name || "";

    const ruleMap = {
      AppGroupProcess: 'includeProcess',
      AppGroupFileAccess: 'includeFileAccess',
      AppGroupRegistry: 'includeRegistryAccess',
      AppGroupCodeLoad: 'includeCodeLoad',
      AppGroupChildLaunch: 'includeChildLaunch',
    };
    for (const [jsonKey, optionKey] of Object.entries(ruleMap)) {
      const arr = parsed[jsonKey];
      formData.importOptions[optionKey] = Array.isArray(arr) && arr.length > 0;
    }

    visible.value = true;
  } catch {
    Swal.fire("Error", "Invalid JSON file!", "error");
  }
};
  reader.readAsText(file);
};

const onCancel = () => {
  visible.value = false;
  formData.name = "";
  formData.fileName = "";
  formData.importedData = null;
  formData.importVersion = null;
  Object.assign(formData.importOptions, {
    createNew: null,
    existingAppGroupId: null,
    includeProcess: false,
    includeFileAccess: false,
    includeRegistryAccess: false,
    includeCodeLoad: false,
    includeChildLaunch: false,
  });
  if (fileInput.value) fileInput.value.value = "";
};


const onSave = async (updatedForm) => {
  try {
    if (!updatedForm?.importedData) {
      return Swal.fire("Error", "Please select a valid JSON file first.", "error");
    }

    const opts = updatedForm.importOptions;

    if (opts.createNew === null) {
      return Swal.fire("Error", "Please select a destination.", "error");
    }

    if (!opts.createNew && !opts.existingAppGroupId) {
      return Swal.fire("Error", "Please select an existing App Group.", "error");
    }

    const hasSelectedRule = opts.includeProcess || opts.includeFileAccess || opts.includeRegistryAccess || opts.includeCodeLoad || opts.includeChildLaunch;
    if (!hasSelectedRule) {
      return Swal.fire("Error", "Please select at least one rule to upload.", "error");
    }

    const group = localStorage.getItem("agms_group");
    const groupId = group ? JSON.parse(group)?.mainid : null;

    const payload = {
      Version: updatedForm.importVersion || "1.0",
      AppGroup: {
        ...updatedForm.importedData,
        Name: updatedForm.name,
        GroupId: groupId,
        Enabled: false,
      },
      ImportOptions: {
        CreateNew: opts.createNew,
        ExistingAppGroupId: opts.existingAppGroupId ?? null,
        IncludeProcess: opts.includeProcess,
        IncludeFileAccess: opts.includeFileAccess,
        IncludeRegistryAccess: opts.includeRegistryAccess,
        IncludeCodeLoad: opts.includeCodeLoad,
        IncludeChildLaunch: opts.includeChildLaunch,
      },
    };

    loading.value = true;

    const response = await store.dispatch(
      GeneralActionTypes.IMPORT_APPGROUP,
      payload
    );

    if (response?.isSuccess) {
      const warnings = response.entityData?.warnings ?? [];
      const imported = warnings.filter(w => w.key === 'rulesImported');
      const skipped = warnings.filter(w => w.key === 'duplicateRulesSkipped');
      const publisherWarnings = warnings.filter(w => w.key?.startsWith('publisherNotFound'));

      if (warnings.length > 0) {
        const sections = [];

        if (imported.length > 0) {
          const rows = imported
            .map(w => `<li>${w.params.collection}: <strong>${w.params.count}</strong> rule(s) added</li>`)
            .join('');
          sections.push(`<div style="margin-bottom: 12px;"><div style="font-weight: 600; margin-bottom: 4px;">Imported</div><ul style="margin: 0; padding-left: 20px; list-style: disc;">${rows}</ul></div>`);
        }

        if (skipped.length > 0) {
          const rows = skipped
            .map(w => `<li>${w.params.collection}: <strong>${w.params.count}</strong> rule(s) skipped (already exist)</li>`)
            .join('');
          sections.push(`<div style="margin-bottom: 12px;"><div style="font-weight: 600; margin-bottom: 4px;">Skipped</div><ul style="margin: 0; padding-left: 20px; list-style: disc;">${rows}</ul></div>`);
        }

        if (publisherWarnings.length > 0) {
          const rows = publisherWarnings
            .map(w => `<li>${t(w.key, w.params)}</li>`)
            .join('');
          sections.push(`<div style="margin-bottom: 12px;"><div style="font-weight: 600; margin-bottom: 4px;">Warnings</div><ul style="margin: 0; padding-left: 20px; list-style: disc;">${rows}</ul></div>`);
        }

        const summaryHtml = `<div style="text-align: left; font-size: 14px; line-height: 1.6;">${sections.join('')}</div>`;

        await Swal.fire({
          icon: publisherWarnings.length > 0 ? "warning" : "success",
          title: publisherWarnings.length > 0 ? "Import completed with warnings" : "Import completed",
          html: summaryHtml,
        });
      } else {
        Swal.fire("Import Complete", "AppGroup imported successfully!", "success");
      }
      await store.dispatch(GeneralActionTypes.GET_APPGROUPS);
      router.push("/administration/app-group-management");
      onCancel();
    } else {
      Swal.fire("Error", response?.message || "Import failed.", "error");
    }
  } catch (err) {
    console.error(err);
    const serverData = err?.response?.data ?? err?.data ?? null;
    let msg = "";

    if (serverData?.errors) {
      msg = Array.isArray(serverData.errors) ? serverData.errors.join("<br>") : serverData.errors;
    } else if (serverData?.message) {
      msg = serverData.message;
    } else if (err?.message) {
      msg = err.message;
    } else {
      msg = JSON.stringify(err);
    }

    Swal.fire({ icon: "error", title: "Error", html: msg || "Failed to import AppGroup." });
  } finally {
    loading.value = false;
  }
};

defineExpose({ open });
</script>

<style scoped>
.global-loading {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(255, 255, 255, 0.349) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99999 !important;
}
</style>

<style>
.edit-panel-container:has(.form-wrapper-process) > div:nth-child(2) {
  flex: none !important;
  max-height: none !important;
  overflow: visible !important;
}
</style>